import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'utils/withRouter';
import Spinner from 'common/spinner/components/spinner';
import SidePanel from 'common/side-panel/side-panel';
import moment from 'utils/cops-moment';
import ShortSteps from 'common/publish-steps/containers/short-steps-container';
import { Form } from '@ant-design/compatible';

import {
  FormItem,
  DatePicker,
  InputNumber,
  TimePicker,
  Flex,
  Button,
  Input,
} from 'ui';
import settingsConfig from '../../settings-config';

const StyledBlock = styled.div`
  padding-top: 16px;
  margin-bottom: 10px;
`;

const StyledSaveButton = styled(Button)`
  text-transform: uppercase;
`;

const StyledFormItem = styled(FormItem)`
  .ant-legacy-form-item-required::before {
    display: none;
  }
`;

const StyledRightFormItem = styled(StyledFormItem)`
  .ant-legacy-form-item-label {
    margin-left: 140px !important;
  }
`;

const Unit = styled.span`
  margin-left: 5px;
  font-size: 16px;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 125px !important;
`;

const Right = styled.div`
  float: right;
`;

const CancelButton = styled(Button)`
  margin-right: 12px;
  width: 140px;
  text-transform: uppercase;
`;

const StyledInput = styled(Input)`
  width: 55%;
`;

const StyledFlex = styled(Flex)`
  width: 55%;
`;

// eslint-disable-next-line
const AbsenceEditSidepanel = ({
  match,
  history,
  intl,
  absence,
  form,
  isLoadingAbsenceData = false,
  fetchAbsence,
  updateAbsence,
  saveSuccess,
  addAbsence,
  addSuccess,
  activeServiceCenter,
  plantData,
  serviceCenters,
  type,
  fetchAbsenceSuccess,
  updateAbsenceId,
  isLoading,
  isPolling,
  error,
  updateProgressIndicator,
}) => {
  const { getFieldDecorator } = form;
  const isNew = !match.params.id;
  const plant = plantData?.plant;
  const region = plantData?.region;
  const fse = plantData?.fse;

  useEffect(() => {
    if (!isNew) {
      fetchAbsence({ id: match.params.id });
    }
  }, []);

  useEffect(() => {
    if (Object.keys(error).length && error.status === 410) {
      fetchAbsence({ id: error.actions?.find(a => a.id === 'reload').toId });
      updateAbsenceId({
        from: error.actions?.find(a => a.id === 'reload').fromId,
        to: error.actions?.find(a => a.id === 'reload').toId,
      });
    }
  }, [error]);

  const close = () => {
    history.back();
  };

  useEffect(() => {
    if (saveSuccess || addSuccess) {
      close();
    }
  }, [saveSuccess, addSuccess]);

  useEffect(() => {
    if (absence && !absence.operationInfo) {
      updateProgressIndicator({ id: absence.id, indicator: null });
    }
  }, [absence]);

  const updateAbsenceData = () => {
    updateAbsence({
      id: absence?.id,
      description: form.getFieldValue('description'),
      start: moment(
        // eslint-disable-next-line
        `${form.getFieldValue('startDate').format('YYYY_MM_DD')} ${form.getFieldValue('startTime').format('HH:mm:ss')}`,
        'YYYY-MM-DD HH:mm:ss',
      ).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
      duration: form.getFieldValue('duration'),
    });
  };

  const getId = () => {
    if (type === settingsConfig.types.plant) {
      return plant?.id;
    } else if (type === settingsConfig.types.region) {
      return region?.id;
    } else if (type === settingsConfig.types.fse) {
      return fse?.id;
    }
    return null;
  };

  const addAbsenceData = () => {
    addAbsence({
      id: getId(),
      description: form.getFieldValue('description'),
      start: moment(
        // eslint-disable-next-line
        `${form.getFieldValue('startDate').format('YYYY_MM_DD')} ${form.getFieldValue('startTime').format('HH:mm:ss')}`,
        'YYYY-MM-DD HH:mm:ss',
      ).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
      duration: form.getFieldValue('duration'),
    });
  };

  const updateIndicator = (cbType) => {
    if (cbType === 'done') {
      updateProgressIndicator({ id: absence.id, indicator: null });
    } else if (cbType === 'error') {
      updateProgressIndicator({ id: absence.id, indicator: 'error' });
    }
  };

  const handleSave = () => {
    form.validateFields((err) => {
      if (!err) {
        if (isNew) {
          addAbsenceData();
        } else {
          updateAbsenceData();
        }
      }
    });
  };

  const serviceCenterValidator = getFieldDecorator('serviceCenter', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.SERVICE_CENTER' }) }],
    initialValue: serviceCenters?.find(s => s.id === activeServiceCenter)?.name,
  });

  const plantValidator = getFieldDecorator('plant', {
    rules: [{
      required:
        type === settingsConfig.types.plant ||
        type === settingsConfig.types.region ||
        type === settingsConfig.types.fse,
      message: intl.formatMessage({ id: 'rules.PLANT' }),
    }],
    initialValue: plant?.name,
  });

  const regionValidator = getFieldDecorator('region', {
    rules: [{
      required:
        type === settingsConfig.types.region ||
        type === settingsConfig.types.fse,
      message: intl.formatMessage({ id: 'rules.REGION' }),
    }],
    initialValue: region?.name,
  });

  const fseValidator = getFieldDecorator('fse', {
    rules: [{
      required:
        type === settingsConfig.types.fse,
      message: intl.formatMessage({ id: 'rules.FSE' }),
    }],
    initialValue: fse?.name,
  });

  const startDateValidator = getFieldDecorator('startDate', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_DATE' }) }],
    initialValue: isNew ? moment() : moment(absence?.start),
  });

  const startWorkingDay = '00:00:00';
  const startTimeValidator = getFieldDecorator('startTime', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_TIME' }) }],
    initialValue: isNew ? moment(startWorkingDay, 'HH:mm:ss') : moment(absence?.start),
  });

  const descriptionAbsenceValidator = getFieldDecorator('description', {
    rules: [{
      required: true,
      message: intl.formatMessage({ id: 'rules.DESCRIPTION_ABSENCE_ERROR' }),
    }],
    initialValue: absence?.description,
  });

  const durationAbsenceValidator = getFieldDecorator('duration', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.DURATION_ERROR' }) },
    ],
    initialValue:
      // eslint-disable-next-line
      isNew ? moment.duration('24:00:00').asHours() : absence?.duration,
  });

  return (
    <SidePanel
      width={'40%'}
      title={
        intl.formatMessage({ id: 'holidays.HOLIDAYS_ABSENCE_TITLE' })
      }
      onPanelClose={close}
      scrollOnOverflow={false}
    >
      {!isLoadingAbsenceData ?
        <>
          <ShortSteps
            id={absence?.id}
            reloadData={
              id => fetchAbsence({ id })
            }
            onDataReloaded={fetchAbsenceSuccess}
            type='unavailability'
            updateId={(from, to) => updateAbsenceId({ from, to })}
            isSidePanel={true}
            errorOrDoneCallback={updateIndicator}
          />
          <StyledBlock>
            <Form layout='vertical'>
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'holidays.CUSTOMER_CENTER' })
                }
              >
                {serviceCenterValidator(
                  <StyledInput
                    disabled={true}
                    placeholder={intl.formatMessage({
                      id: 'holidays.NO_DATA',
                    })}
                  />,
                )}
              </StyledFormItem>
              {type === settingsConfig.types.plant ||
                type === settingsConfig.types.region ||
                type === settingsConfig.types.fse ?
                plantValidator(
                  <StyledFormItem
                    label={
                      intl.formatMessage({ id: 'holidays.PLANT' })
                    }
                  >
                    {plantValidator(
                      <StyledInput
                        disabled={true}
                        placeholder={intl.formatMessage({
                          id: 'holidays.NO_DATA',
                        })}
                      />,
                    )}
                  </StyledFormItem>,
                ) :
                null
              }
              {type === settingsConfig.types.region ||
                type === settingsConfig.types.fse ?
                regionValidator(
                  <StyledFormItem
                    label={
                      intl.formatMessage({ id: 'holidays.REGION' })
                    }
                  >
                    {regionValidator(
                      <StyledInput
                        disabled={true}
                        placeholder={intl.formatMessage({
                          id: 'holidays.NO_DATA',
                        })}
                      />,
                    )}
                  </StyledFormItem>,
                ) :
                null
              }
              {type === settingsConfig.types.fse ?
                fseValidator(
                  <StyledFormItem
                    label={
                      intl.formatMessage({ id: 'holidays.FSE' })
                    }
                  >
                    {fseValidator(
                      <StyledInput
                        disabled={true}
                        placeholder={intl.formatMessage({
                          id: 'holidays.NO_DATA',
                        })}
                      />,
                    )}
                  </StyledFormItem>,
                ) :
                null
              }
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'holidays.DESCRIPTION' })
                }
              >
                {descriptionAbsenceValidator(
                  <StyledInput
                    placeholder={intl.formatMessage({
                      id: 'holidays.DESCRIPTION_PLACEHOLDER',
                    })}
                  />,
                )}
              </StyledFormItem>
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'holidays.START_DATE' })
                }
              >
                {
                  startDateValidator(
                    <DatePicker
                      disabled={isLoadingAbsenceData}
                      allowPast={false}
                    />,
                  )
                }
              </StyledFormItem>
              <StyledFlex ffrn jcsb>
                <StyledFormItem
                  label={
                    intl.formatMessage({ id: 'holidays.START_TIME' })
                  }
                >
                  {startTimeValidator(
                    <StyledTimePicker
                      allowClear={false}
                      showSecond={false}
                      format='HH:mm'
                      disabled={isLoadingAbsenceData}
                      minuteStep={15}
                    />,
                  )}
                </StyledFormItem>
                <StyledRightFormItem
                  label={intl.formatMessage({ id: 'holidays.DURATION' })}
                >
                  <Right>
                    {durationAbsenceValidator(
                      <InputNumber
                        min={0}
                        step={1}
                        precision={0}
                        disabled={isLoadingAbsenceData}
                      />,
                    )}
                    <Unit><FormattedMessage id='equipment.taskList.uom.STD' /></Unit>
                  </Right>
                </StyledRightFormItem>
              </StyledFlex>
              <Flex jcfs marginTop={20}>
                <CancelButton type='secondary' light onClick={close}>
                  {intl.formatMessage({ id: 'common.CANCEL' })}
                </CancelButton>
                <StyledSaveButton
                  onClick={handleSave}
                  disabled={isLoadingAbsenceData || isLoading || isPolling}
                  type='secondary'
                >
                  {intl.formatMessage({ id: 'holidays.SAVE' })}
                </StyledSaveButton>
              </Flex>
            </Form>
          </StyledBlock>
        </> :
        <Spinner />
      }
    </SidePanel>
  );
};

AbsenceEditSidepanel.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
  absence: PropTypes.object,
  form: PropTypes.object,
  isLoadingAbsenceData: PropTypes.bool,
  fetchAbsence: PropTypes.func,
  updateAbsence: PropTypes.func,
  saveSuccess: PropTypes.bool,
  addAbsence: PropTypes.func,
  addSuccess: PropTypes.bool,
  plantData: PropTypes.object,
  activeServiceCenter: PropTypes.string,
  serviceCenters: PropTypes.array,
  type: PropTypes.string,
  fetchAbsenceSuccess: PropTypes.func,
  updateAbsenceId: PropTypes.func,
  isLoading: PropTypes.bool,
  isPolling: PropTypes.bool,
  error: PropTypes.object,
  updateProgressIndicator: PropTypes.func,
};

export default Form.create()(withRouter(injectIntl(AbsenceEditSidepanel)));
